/**
 * Google Identity Services (GIS) wrapper.
 * Loads the script once, exposes a renderButton helper bound to the configured client ID,
 * and surfaces id_token credentials via a callback.
 */

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: GisInitConfig) => void;
          renderButton: (parent: HTMLElement, options: GisButtonOptions) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

interface GisInitConfig {
  client_id: string;
  callback: (response: { credential: string }) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  ux_mode?: 'popup' | 'redirect';
  context?: 'signin' | 'signup' | 'use';
}

interface GisButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'small' | 'medium' | 'large';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  width?: number | string;
  logo_alignment?: 'left' | 'center';
}

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let loadingPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (loadingPromise) return loadingPromise;
  loadingPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('failed to load gis')), {
        once: true,
      });
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener('load', () => resolve(), { once: true });
    s.addEventListener('error', () => reject(new Error('failed to load gis')), { once: true });
    document.head.appendChild(s);
  });
  return loadingPromise;
}

export async function renderGoogleButton(
  parent: HTMLElement,
  clientId: string,
  onCredential: (idToken: string) => void,
): Promise<void> {
  await loadScript();
  const gis = window.google?.accounts.id;
  if (!gis) throw new Error('google identity unavailable');
  gis.initialize({
    client_id: clientId,
    callback: (resp) => onCredential(resp.credential),
    auto_select: false,
    cancel_on_tap_outside: true,
    ux_mode: 'popup',
    context: 'signin',
  });
  parent.replaceChildren();
  gis.renderButton(parent, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    shape: 'rectangular',
    text: 'signin_with',
    width: 320,
    logo_alignment: 'left',
  });
}

export function disableGoogleAutoSelect(): void {
  window.google?.accounts.id.disableAutoSelect();
}
